import React from 'react';
import CustomGraph from '_components/graphs/Custom.graph';

const DataBusSocGraphs = ({ busDetails }) => {

    const info = busDetails?.warnings?.batterie || [];
    
    const filteredData = info
        .map(item => ({
            DATE: new Date(item.DATE),
            SOC: item.SOC !== null && item.SOC !== 0 ? item.SOC : null, 
            PCKS: item.PCKS !== null && item.PCKS !== 0 ? item.PCKS : null,
            DST: (item.DST !== null && item.DST !== 0 && item.DST !== "0") ? item.DST : null,
        }))
        .filter(item => item.SOC !== null || item.PCKS !== null || item.DST !== null);
    const latestDate = info[info.length - 1]?.DATE;


    // Données et configuration pour le graph de SOC
    const socData = {
        labels: filteredData.map(item => new Date(item.DATE)),
        datasets: [
            {
                label: 'SOC (%)',
                data: filteredData.map(item => item.SOC),
                fill: false,
                borderColor: 'blue',
                backgroundColor: 'rgba(0, 174, 255, 0.3)',
                tension: 0, // Pour une courbe lisse
                yAxisID: 'y2',
                spanGaps: false,
            },
            {
                label: 'Distance',
                data: filteredData.map(item => item.DST !== null ? item.DST / 1000 : null),
                fill: false,
                borderColor: 'red',
                backgroundColor: 'rgba(255, 0, 0, 0.781)',
                tension: 0,
                yAxisID: 'y1',
                spanGaps: false,
            }
        ]
    };

    // Données et configuration pour le graph de PCKS
    const pcksData = {
        labels: filteredData.map(item => new Date(item.DATE)),
        datasets: [
            {
                label: 'Packs',
                data: filteredData.map(item => item.PCKS),
                fill: false,
                borderColor: 'green',
                backgroundColor: 'rgba(58, 245, 21, 0.3)',
                tension: 0,
                yAxisID: 'y1',
                spanGaps: false,
            },
            {
                label: 'SOC (%)',
                data: filteredData.map(item => item.SOC),
                fill: false,
                borderColor: 'blue',
                backgroundColor: 'rgba(9, 157, 243, 0.3)',
                tension: 0, // Pour une courbe lisse
                yAxisID: 'y2',
                spanGaps: false,
            }
        ]
    };

    const baseOptions = {
        responsive: true,
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    tooltipFormat: 'yyyy-MM-dd HH:mm:ss',
                },
                grid: {
                    display: true, // Désactive les lignes verticales
                    color: 'rgba(0, 0, 0, 0.1)', // Couleur des lignes de grille
                    borderDash: [5, 5]
                },
                ticks: {
                    maxRotation: 0, // Rotation des labels à 0° pour éviter les chevauchements
                    autoSkip: true, // Active le saut automatique des labels
                    maxTicksLimit: 10, // Limite le nombre de ticks affichés
                    callback: (value) => {
                        // Formate les labels pour une meilleure lisibilité
                        return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    },
                },
            },           
        },
        plugins: {
            tooltip: {
                enabled: true, // Affiche les données au survol
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x', // Permet de déplacer horizontalement
                },
                zoom: {
                    wheel: {
                        enabled: true, // Active le zoom à la molette
                    },
                    pinch: {
                        enabled: true, // Active le zoom via pincement (tactile)
                    },
                    mode: 'x', // Zoom horizontal uniquement
                    speed: 0.1, // Vitesse de zoom
                },
            },
            annotation: {
                annotations: {
                    dateLabel: {
                        type: 'label',
                        xValue: latestDate, // Date la plus récente
                        yValue: 100, // Position verticale
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                        borderWidth: 1,
                        font: {
                            size: 12,
                            weight: 'bold',
                        },
                        padding: {
                            top: 4,
                            bottom: 4,
                        },
                        label: {
                            content: `Dernière date: ${new Date(latestDate).toLocaleDateString()}`, // Date affichée
                            enabled: true,
                            position: 'top', // Position du label
                        },
                    },
                },
            },
        },
        elements: {
            line: {
                tension: 0, // Lissage de la courbe
                spanGaps: false, //évite les connexions sur les valeurs nulles
            }
        }
    };

    // Configuration pour le premier graphique
    const socOptions = {
        ...baseOptions,
        scales: {
            ...baseOptions.scales,            
            y1: { // Axe Y pour DST                
                beginAtZero: false,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                },   
                ticks: {
                    callback: (value) => `${value} km`,
                },             
            },
            y2: { // Axe Y pour SOC                
                suggestedMax: 100, 
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    callback: (value) => `${value}%`,
                },
            },
        },
    };

    // Configuration pour le deuxième graphique: SOC + Nb Packs
    const packsOptions = {
        ...baseOptions,
        scales: {
            ...baseOptions.scales,
             y1: { // Axe Y pour PCKS
                ...baseOptions.scales.y,
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                },   
                suggestedMax: 14,             
            },
            y2: { // Axe Y pour SOC
                ...baseOptions.scales.y,
                suggestedMax: 100, 
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    callback: (value) => `${value}%`,
                },
            },
            
        },
    };



    return (
        <div className="flex w-full">
            {/* Graphique SOC */}
            <div className="w-[45%] m-auto graph-card">
                <div className="xsmall-title-customtable mb-4">
                    Evolution du SOC / Distance
                </div>
                <CustomGraph data={socData} options={socOptions} />
            </div>

            {/* Graphique PCKS */}
            <div className="w-[45%] m-auto graph-card">
                <div className="xsmall-title-customtable mb-4">
                    Packs connectés / SOC
                </div>
                <CustomGraph data={pcksData} options={packsOptions} />
            </div>
        </div>
    );
};

export default DataBusSocGraphs;
